import { useAppSelector } from '@hooks/app/redux';
import { PlusOutlined } from '@icon';
import { getWorkspaceDetail } from '@services/external/provider.api';
import { Authorized, IconButton, Table } from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { TAuthority } from 'types/authority.types';
import { IWorkspaceWorker } from 'types/workspace.types';
import DepartmentStaffEdit from './components/DepartmentStaffEdit';
import { DepartmentsStaffWrapper } from './DepartmentsStaff.styled';
import { useTableColumns } from './DepartmentsStaff.utils';

const DEPARTMENT_AUTH: TAuthority[] = ['workerManagementEdit'];

interface RouteParams {
  workspaceId: string;
}

const DepartmentsStaff: React.FunctionComponent = () => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const {
    params: { workspaceId },
  } = useRouteMatch<RouteParams>();

  const providerId = useAppSelector((state) => state.users.currentProvider?.id);
  const isMounted = React.useRef(false);

  const [currentWorker, setCurrentWorker] = React.useState<Partial<IWorkspaceWorker> | undefined>();

  const {
    isLoading: workspaceDataLoading,
    data: workspaceData,
    refetch,
  } = useQuery(['getWorkspace', workspaceId, providerId], async () => {
    const { data: axData } = await getWorkspaceDetail(providerId as string, workspaceId as string);

    return axData.data;
  });

  React.useEffect(() => {
    if (isMounted.current) {
      history.replace('/business/profile/departments');
    }

    isMounted.current;
  }, [providerId]);

  const closeForm = React.useCallback(() => {
    setCurrentWorker(undefined);
  }, []);

  const afterSubmit = React.useCallback(() => {
    setCurrentWorker(undefined);
    refetch();
  }, []);

  const openForm = React.useCallback((worker?: IWorkspaceWorker) => {
    setCurrentWorker(worker ?? {});
  }, []);

  const columns = useTableColumns({ onEditClick: openForm });
  const data = workspaceData?.workers ?? [];

  return (
    <>
      <DepartmentsStaffWrapper
        title={`${formatMessage({ id: 'routes.business_profile_departments_staff' })} - ${
          workspaceData?.name ?? ''
        }`}
        onBack={() => {
          history.goBack();
        }}
      >
        <Authorized authority={DEPARTMENT_AUTH}>
          <Table.Header
            title={`Pracovníci oddelenia (${data.length ?? 0})`}
            extra={<IconButton Icon={PlusOutlined} size="medium" onClick={() => openForm()} />}
          />
          <Table
            loading={workspaceDataLoading}
            columns={columns}
            data={data}
            showEmpty
            emptyTitle="Žiadni pracovníci"
          />
        </Authorized>
      </DepartmentsStaffWrapper>
      <DepartmentStaffEdit
        visible={!!currentWorker}
        onClose={closeForm}
        worker={currentWorker}
        workspaceId={workspaceId}
        afterSubmit={afterSubmit}
      />
    </>
  );
};

export default DepartmentsStaff;
